"use client";

import { useState } from "react";
import { Button, Flex, Tag, Modal } from "antd";
import dayjs from "dayjs";
import {
  IconChevronDown,
  IconMapPin,
  IconMinus,
  IconPlus,
  IconTicket,
} from "@/lib/icons";
import styles from "./booking.module.css";
import BorderLogo from "../../images/tykkit_border_text.png";
import { formatCurrency } from "@/lib/formatCurrency";
import { getTicketTheme } from "./ticketTheme";

function formatPassDateTime(date, time) {
  if (!date) return null;
  const parsed = time
    ? dayjs(
        `${date} ${time}`,
        ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"],
        true,
      )
    : dayjs(date, "YYYY-MM-DD", true);
  if (!parsed.isValid()) {
    const fallback = time ? dayjs(`${date} ${time}`) : dayjs(date);
    if (!fallback.isValid()) return null;
    return time
      ? fallback.format("ddd, DD MMM YYYY • hh:mm A")
      : fallback.format("ddd, DD MMM YYYY");
  }
  return time
    ? parsed.format("ddd, DD MMM YYYY • hh:mm A")
    : parsed.format("ddd, DD MMM YYYY");
}

function formatValidity(start, end) {
  if (!start && !end) return null;
  const s = start ? dayjs(start) : null;
  const e = end ? dayjs(end) : null;
  if (s?.isValid() && e?.isValid()) {
    if (s.isSame(e, "day")) return s.format("ddd, DD MMM YYYY");
    return `${s.format("ddd, DD MMM YYYY")} – ${e.format("ddd, DD MMM YYYY")}`;
  }
  if (s?.isValid()) return s.format("ddd, DD MMM YYYY");
  if (e?.isValid()) return e.format("ddd, DD MMM YYYY");
  return null;
}

export function placeLabel(leg) {
  const city = String(leg?.city || "").trim();
  const venue = String(leg?.venue || "").trim();
  if (city && venue && city.toLowerCase() === venue.toLowerCase()) {
    return city;
  }
  if (city && venue) return `${city} — ${venue}`;
  return city || venue || leg?.eventName || "Event";
}

export function readableTicketDescription(raw) {
  if (!raw) return null;
  const text = String(raw)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text || text.length < 2) return null;
  return text.length > 100 ? `${text.slice(0, 100)}…` : text;
}

export function SeasonPassCoveredEvents({
  covered = [],
  highlightChildEventId = null,
  highlightDate = null,
  highlightTime = null,
  className = "",
}) {
  if (!covered.length) return null;
  return (
    <div
      className={[
        "rounded-xl border border-[#e3e6e9] bg-[#f8f9fb] px-3.5 py-3",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[rgba(2,21,38,0.55)]">
        Pass covers {covered.length}{" "}
        {covered.length === 1 ? "event" : "events"}
      </p>
      <ul className="m-0 p-0 list-none flex flex-col gap-2">
        {covered.map((leg, idx) => {
          const isAnchor =
            (highlightChildEventId &&
              leg.childEventId === highlightChildEventId) ||
            (highlightDate &&
              leg.date === highlightDate &&
              (!highlightTime || leg.startTime === highlightTime));
          const startWhen = formatPassDateTime(leg.date, leg.startTime);
          const hasEnd =
            Boolean(leg.endTime) ||
            (Boolean(leg.endDate) && leg.endDate !== leg.date);
          const endWhen = hasEnd
            ? formatPassDateTime(leg.endDate || leg.date, leg.endTime)
            : null;
          const showEnd = Boolean(endWhen) && endWhen !== startWhen;
          return (
            <li
              key={`${leg.childEventId}-${leg.date}-${leg.startTime}-${idx}`}
              className={[
                "flex items-start gap-2",
                isAnchor ? "text-[#021526]" : "text-[rgba(2,21,38,0.72)]",
              ].join(" ")}
            >
              <IconMapPin
                size={14}
                className={[
                  "mt-0.5 shrink-0",
                  isAnchor ? "text-[#021526]" : "text-[#94a3b8]",
                ].join(" ")}
              />
              <div className="min-w-0 flex flex-col gap-0.5">
                <span
                  className={[
                    "text-[13px] leading-snug",
                    isAnchor ? "font-semibold" : "font-medium",
                  ].join(" ")}
                >
                  {placeLabel(leg)}
                </span>
                {startWhen ? (
                  <span className="text-[12px] leading-snug text-[rgba(2,21,38,0.55)]">
                    <span className="font-medium text-[rgba(2,21,38,0.7)]">
                      Starts:
                    </span>{" "}
                    {startWhen}
                  </span>
                ) : null}
                {showEnd ? (
                  <span className="text-[12px] leading-snug text-[rgba(2,21,38,0.55)]">
                    <span className="font-medium text-[rgba(2,21,38,0.7)]">
                      Ends:
                    </span>{" "}
                    {endWhen}
                  </span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function SeasonPassCoveredEventsModal({
  covered = [],
  highlightChildEventId = null,
  highlightDate = null,
  highlightTime = null,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  if (!covered.length) return null;
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-label={`Show ${covered.length} included events`}
        className={[
          "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs font-semibold cursor-pointer transition-colors mt-1.5",
          open
            ? "border-[#021526] bg-[#021526] text-white"
            : "border-[#cfd6dd] bg-white text-[#021526] hover:bg-[#f3f6f9]",
        ].join(" ")}
      >
        <IconTicket size={14} stroke={2} />
        <span>
          {covered.length} {covered.length === 1 ? "event" : "events"}
        </span>
        <IconChevronDown
          size={14}
          stroke={2}
          className={[
            "transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        title="Covered Events"
        destroyOnClose
        centered
        width={500}
        styles={{
          body: { maxHeight: "70vh", overflowY: "auto", padding: "12px 0 0" },
        }}
      >
        <SeasonPassCoveredEvents
          covered={covered}
          highlightChildEventId={highlightChildEventId}
          highlightDate={highlightDate}
          highlightTime={highlightTime}
          className="!border-0 !bg-transparent !p-0"
        />
      </Modal>
    </div>
  );
}

/**
 * Compact season pass card (same shell as day tickets).
 * Events open via a clear “Events” control under the price.
 */
export default function SeasonPassTicketCard({
  pass,
  index = 0,
  selected = false,
  quantity = 0,
  onSelect,
  onQuantityChange,
  highlightChildEventId = null,
  highlightDate = null,
  highlightTime = null,
  isUniqueRegistration = false,
}) {
  const [eventsOpen, setEventsOpen] = useState(false);
  const theme = getTicketTheme(index, pass?.id);
  const validity = formatValidity(pass?.validityStart, pass?.validityEnd);
  const covered = Array.isArray(pass?.coveredEvents) ? pass.coveredEvents : [];
  const description = readableTicketDescription(pass?.description);
  const minQty = Number(pass?.min_per_transaction || 1);
  const maxQty = Number(pass?.max_per_transaction || minQty);
  const available = Number(
    pass?.available ?? pass?.available_quantity ?? maxQty,
  );
  const effectiveMax = isUniqueRegistration
    ? 1
    : Math.min(
        maxQty,
        Number.isFinite(available) && available > 0 ? available : maxQty,
      );
  const effectiveMin = isUniqueRegistration ? 1 : minQty;
  const eventCount = pass?.eventCount || covered.length;
  const isSaleClosed = pass?.status === "closed";

  const handleDecrease = () => {
    const next = Math.max(effectiveMin, (quantity || effectiveMin) - 1);
    onQuantityChange?.(pass, next);
  };

  const handleIncrease = () => {
    const next = Math.min(effectiveMax, (quantity || effectiveMin) + 1);
    onQuantityChange?.(pass, next);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className={styles.ticket}
        style={
          selected
            ? {
                borderColor: "#021526",
                boxShadow: "0 0 0 2px rgba(2,21,38,0.1)",
              }
            : undefined
        }
      >
        <div className={styles.border} style={{ backgroundColor: theme.bg }}>
          <img src={BorderLogo.src} alt="tykkit" />
        </div>

        <div className={styles.body}>
          <Flex vertical gap={6} style={{ minWidth: 0, flex: 1 }}>
            <div className={styles.title}>{pass?.name}</div>

            {description ? (
              <div className="text-gray-500 overflow-hidden break-words text-sm">
                {description}
              </div>
            ) : null}

            <div className={styles.pricing}>
              {formatCurrency(pass?.price, pass?.currency || "INR")}
            </div>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              {validity ? (
                <span className="text-xs text-[rgba(2,21,38,0.65)]">
                  Valid: {validity}
                </span>
              ) : null}

              {covered.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setEventsOpen((v) => !v)}
                  aria-expanded={eventsOpen}
                  aria-label={`${eventsOpen ? "Hide" : "Show"} ${eventCount} included events`}
                  className={[
                    "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs font-semibold cursor-pointer transition-colors",
                    eventsOpen
                      ? "border-[#021526] bg-[#021526] text-white"
                      : "border-[#cfd6dd] bg-white text-[#021526] hover:bg-[#f3f6f9]",
                  ].join(" ")}
                >
                  <IconTicket size={14} stroke={2} />
                  <span>
                    {eventCount} {eventCount === 1 ? "event" : "events"}
                  </span>
                  <IconChevronDown
                    size={14}
                    stroke={2}
                    className={[
                      "transition-transform",
                      eventsOpen ? "rotate-180" : "",
                    ].join(" ")}
                  />
                </button>
              ) : eventCount > 0 ? (
                <span className="text-xs text-[rgba(2,21,38,0.65)]">
                  {eventCount} {eventCount === 1 ? "event" : "events"} included
                </span>
              ) : null}
            </div>
          </Flex>

          <div className="shrink-0 self-center">
            {isSaleClosed ? (
              <Tag
                color="blue"
                className="!px-3 !py-2 !mx-0"
                style={{ borderWidth: 1, borderStyle: "solid", borderColor: "#91caff" }}
              >
                Sale Closed
              </Tag>
            ) : selected ? (
              isUniqueRegistration ? (
                <Tag color="success" className="!px-3 !py-2 !mx-0">
                  Added
                </Tag>
              ) : (
                <div className={styles.quantityButton}>
                  <Button
                    icon={<IconMinus size={16} />}
                    style={{ lineHeight: 1 }}
                    onClick={handleDecrease}
                    disabled={quantity <= effectiveMin}
                  />
                  <span>{quantity || effectiveMin}</span>
                  <Button
                    icon={<IconPlus size={16} />}
                    style={{ lineHeight: 1 }}
                    disabled={quantity >= effectiveMax}
                    onClick={handleIncrease}
                  />
                </div>
              )
            ) : (
              <Button onClick={() => onSelect?.(pass, index)}>Add</Button>
            )}
          </div>
        </div>
      </div>

      {covered.length > 0 && (
        <Modal
          open={eventsOpen}
          onCancel={() => setEventsOpen(false)}
          footer={null}
          title="Covered Events"
          destroyOnClose
          centered
          width={500}
          styles={{
            body: { maxHeight: "70vh", overflowY: "auto", padding: "12px 0 0" },
          }}
        >
          <SeasonPassCoveredEvents
            covered={covered}
            highlightChildEventId={highlightChildEventId}
            highlightDate={highlightDate}
            highlightTime={highlightTime}
            className="!border-0 !bg-transparent !p-0"
          />
        </Modal>
      )}
    </div>
  );
}
